/**
 * Client details, editable.
 *
 * The field definitions come from biz_lookup_values rather than being hardcoded
 * here, which is what keeps the module service-generic: adding a service line
 * later means seeding rows, not editing this component.
 *
 * These values are the reason the intern can work unsupervised — the approved
 * keyword list and the services the client actually offers are what stop pages
 * being written for things they do not do.
 */

import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type ProfileField, type ProfileFieldDef } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Pencil, Check, X } from 'lucide-react';

export function ClientProfileCard({ engagementId, serviceLineCode, profile }: {
  engagementId: string;
  serviceLineCode?: string;
  profile: ProfileField[];
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [values, setValues] = useState<Record<string, string>>({});

  const { data: defsData } = useQuery({
    queryKey: ['svc', 'profile-field-defs', serviceLineCode],
    queryFn: () => api.getProfileFieldDefs(serviceLineCode),
  });
  const defs = (defsData?.data ?? []) as ProfileFieldDef[];

  // Seed the editor from whatever is stored, so editing never silently blanks a
  // field the user did not touch.
  useEffect(() => {
    if (!editing) return;
    const seed: Record<string, string> = {};
    for (const d of defs) {
      seed[d.field_key] = profile.find((p) => p.field_key === d.field_key)?.field_value ?? '';
    }
    setValues(seed);
  }, [editing, defs, profile]);

  const save = useMutation({
    mutationFn: async () => {
      // One call per changed field. The endpoint is an upsert per key, which
      // keeps a partial failure from wiping the rest.
      const changed = defs.filter((d) => {
        const current = profile.find((p) => p.field_key === d.field_key)?.field_value ?? '';
        return (values[d.field_key] ?? '') !== current;
      });
      for (const d of changed) {
        const r = await api.setEngagementProfileField(engagementId, {
          fieldKey: d.field_key,
          fieldValue: values[d.field_key] || null,
          valueType: d.value_type || 'text',
        });
        if (!r.success) throw new Error(r.error?.message ?? 'Save failed');
      }
      return changed.length;
    },
    onSuccess: (count) => {
      qc.invalidateQueries({ queryKey: ['svc'] });
      setEditing(false);
      toast({ title: count === 0 ? 'No changes' : `Saved ${count} field${count === 1 ? '' : 's'}` });
    },
    onError: (e: Error) => {
      toast({ title: 'Could not save', description: e.message, variant: 'destructive' });
    },
  });

  const stored = (key: string) => profile.find((p) => p.field_key === key)?.field_value || '';
  const longField = (key: string) => key === 'services_offered' || key === 'target_keywords';

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">Client details</CardTitle>
          {editing ? (
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                <X className="h-4 w-4" />
              </Button>
              <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
                <Check className="h-4 w-4 mr-1.5" /> Save
              </Button>
            </div>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
              <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {defs.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No field definitions for this service line.
          </p>
        ) : editing ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {defs.map((d) => (
              <div key={d.field_key} className={longField(d.field_key) ? 'sm:col-span-2' : ''}>
                <Label className="text-xs">{d.label}</Label>
                {longField(d.field_key) ? (
                  <Textarea
                    className="mt-1" rows={2}
                    value={values[d.field_key] ?? ''}
                    onChange={(e) => setValues((v) => ({ ...v, [d.field_key]: e.target.value }))}
                    placeholder={d.description ?? ''}
                  />
                ) : (
                  <Input
                    className="mt-1"
                    value={values[d.field_key] ?? ''}
                    onChange={(e) => setValues((v) => ({ ...v, [d.field_key]: e.target.value }))}
                    placeholder={d.description ?? ''}
                  />
                )}
              </div>
            ))}
          </div>
        ) : (
          <dl className="grid gap-x-6 gap-y-2.5 text-sm sm:grid-cols-2">
            {defs.map((d) => (
              <div key={d.field_key} className={longField(d.field_key) ? 'sm:col-span-2' : ''}>
                <dt className="text-xs text-muted-foreground">{d.label}</dt>
                <dd className="break-words">
                  {stored(d.field_key) || <span className="text-muted-foreground">—</span>}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </CardContent>
    </Card>
  );
}
